/**
 * The peer connections: one per person in the conversation.
 *
 * This is the part no machine in this project can check. The signalling relay,
 * the roster and the credentials all have tests; whether anybody can actually
 * hear anybody is a person, in two browsers. So the rules that *can* be tested
 * live in `voice-roster.ts`, and what is left here is browser plumbing —
 * deliberately, and kept as thin as it can be.
 *
 * ## The shape of it
 *
 * A mesh. Everybody holds a connection to everybody else, sends their own
 * microphone down each one, and plays what comes back. The server never sees
 * any of it; it only forwards the handshake and publishes who is in.
 *
 * Nothing here decides who is in the conversation. **The roster is the
 * trigger** — `sync` is called with it whenever it changes, and connections are
 * opened and closed to match. That is what makes somebody arriving
 * mid-conversation work without anybody being told to expect them.
 */

import { shouldOffer } from '@cards/protocol';
import { reconcile } from './voice-roster.js';

export type SignalKind = 'offer' | 'answer' | 'candidate' | 'bye';

export interface VoiceSession {
  /** Stop sending audio, without dropping the microphone. */
  setMicOff(off: boolean): void;
  /** Stop playing what arrives. Purely local; nothing is torn down. */
  setEarsOff(off: boolean): void;
  /** Open and close connections so they match the roster. */
  sync(roster: { readonly [userId: string]: unknown }): void;
  /** A blob from a peer, handed over by the match socket. */
  accept(from: string, kind: string, payload: string): void;
  /** Close everything and release the microphone. */
  leave(): void;
}

interface Peer {
  connection: RTCPeerConnection;
  audio: HTMLAudioElement;
  /**
   * Candidates that arrived before there was anywhere to put them.
   *
   * A peer starts trickling candidates the moment it has any, which can be
   * before its offer has been processed at this end. Adding one with no remote
   * description throws, and dropping it silently costs a connection that would
   * otherwise have worked — usually the ones that needed a relay, which are
   * exactly the ones already hardest to get.
   */
  pending: RTCIceCandidateInit[];
  remoteSet: boolean;
}

export interface VoiceOptions {
  myId: string;
  iceServers: RTCIceServer[];
  /** Sends a blob to one peer, through the match socket. */
  signal: (to: string, kind: SignalKind, payload: string) => void;
  /** Something went wrong in a way the player should be told about. */
  onError?: (message: string) => void;
}

/**
 * Whether this browser can do voice at all, and why not when it cannot.
 *
 * Pure, and separated from `joinVoice` for the reason everything testable in
 * this feature is separated: the rest of the file needs a browser, a microphone
 * and somebody at the other end, and this needs none of them.
 *
 * It exists because of one specific dead end. **A phone reaches the dev server
 * over the network as `http://192.168.x.x:5173`, which is not a secure
 * context** — so `navigator.mediaDevices` is not merely empty, it is
 * `undefined`. Calling `getUserMedia` on it throws a `TypeError`, which the
 * join button caught and reported as "No microphone. Check the browser has
 * permission." That sends somebody to check a permission that was never the
 * problem and cannot be granted, on the one device the design is aimed at.
 *
 * `localhost` is exempt from the secure-context rule, which is exactly why this
 * never showed up on a desktop.
 */
export function voiceUnavailable(env: {
  secureContext: boolean;
  hasMediaDevices: boolean;
}): string | null {
  if (!env.secureContext) {
    return 'Voice needs a secure connection. Open this page over https, or on the computer itself.';
  }
  if (!env.hasMediaDevices) {
    return 'This browser cannot record audio, so voice will not work here.';
  }
  return null;
}

/** What `voiceUnavailable` needs, read off the live browser. */
export function browserVoiceSupport(): { secureContext: boolean; hasMediaDevices: boolean } {
  return {
    secureContext: window.isSecureContext,
    hasMediaDevices: typeof navigator.mediaDevices?.getUserMedia === 'function',
  };
}

/**
 * Ask for the microphone and start a conversation.
 *
 * Rejects if permission is refused, which is an ordinary answer rather than an
 * error — a party game that opens by demanding a microphone has lost the room,
 * and the caller is expected to leave the button as it was.
 */
export async function joinVoice(options: VoiceOptions): Promise<VoiceSession> {
  /*
   * One microphone, however many connections.
   *
   * Asked for once and the same track added to every peer. Calling
   * `getUserMedia` per connection means a prompt per connection and a capture
   * device per connection, which on a phone is exactly as bad as it sounds.
   */
  const microphone = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  });

  const peers = new Map<string, Peer>();
  let earsOff = false;
  let closed = false;

  const track = () => microphone.getAudioTracks()[0] ?? null;

  function fail(message: string): void {
    if (options.onError) options.onError(message);
  }

  function open(peerId: string): Peer {
    const connection = new RTCPeerConnection({ iceServers: options.iceServers });

    /*
     * The microphone goes down every connection, muted or not.
     *
     * Muting is `enabled = false` on the track rather than removing it: a
     * removed track renegotiates, and renegotiating every time somebody taps
     * mute would be a conversation full of gaps.
     */
    const mine = track();
    if (mine) connection.addTrack(mine, microphone);

    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.muted = earsOff;
    /*
     * Attached to the document because some browsers will not play audio from
     * an element that is not in it. Hidden, because there is nothing to see.
     */
    audio.style.display = 'none';
    document.body.appendChild(audio);

    const peer: Peer = { connection, audio, pending: [], remoteSet: false };

    connection.ontrack = (event) => {
      const stream = event.streams[0];
      if (stream) audio.srcObject = stream;
    };

    connection.onicecandidate = (event) => {
      if (!event.candidate) return;
      options.signal(peerId, 'candidate', JSON.stringify(event.candidate.toJSON()));
    };

    connection.onconnectionstatechange = () => {
      /*
       * A failed connection is dropped rather than retried here.
       *
       * The roster is the trigger for everything, and it has not changed — so
       * retrying would be this file inventing a policy of its own. A player who
       * cannot hear somebody can leave and rejoin, which re-runs the handshake
       * from a clean state.
       */
      if (connection.connectionState === 'failed') close(peerId);
    };

    peers.set(peerId, peer);
    return peer;
  }

  function close(peerId: string): void {
    const peer = peers.get(peerId);
    if (!peer) return;

    peer.connection.onicecandidate = null;
    peer.connection.ontrack = null;
    peer.connection.onconnectionstatechange = null;
    peer.connection.close();

    peer.audio.srcObject = null;
    peer.audio.remove();
    peers.delete(peerId);
  }

  async function offerTo(peerId: string): Promise<void> {
    const peer = peers.get(peerId);
    if (!peer) return;
    try {
      const offer = await peer.connection.createOffer();
      await peer.connection.setLocalDescription(offer);
      options.signal(peerId, 'offer', JSON.stringify(offer));
    } catch {
      close(peerId);
    }
  }

  /** Anything that arrived early, once there is somewhere to put it. */
  async function drain(peer: Peer): Promise<void> {
    const waiting = peer.pending.splice(0, peer.pending.length);
    for (const candidate of waiting) {
      try {
        await peer.connection.addIceCandidate(candidate);
      } catch {
        // One bad candidate is not worth the connection. The others still work.
      }
    }
  }

  return {
    setMicOff(off) {
      const mine = track();
      /*
       * `enabled = false` keeps the capture device open and keeps sending
       * silence. Stopping the track instead would turn the browser's recording
       * indicator off and on again every time somebody tapped mute, which reads
       * as the app doing something underhand.
       */
      if (mine) mine.enabled = !off;
    },

    setEarsOff(off) {
      earsOff = off;
      // Purely local. Nothing is torn down, and the far end is not told —
      // except by the roster, which the caller updates separately.
      for (const peer of peers.values()) peer.audio.muted = off;
    },

    sync(roster) {
      if (closed) return;
      const { add, drop } = reconcile([...peers.keys()], roster, options.myId);

      for (const peerId of drop) {
        options.signal(peerId, 'bye', '');
        close(peerId);
      }

      for (const peerId of add) {
        open(peerId);
        /*
         * Only one of the pair offers.
         *
         * Both notice each other in the same roster, and if both offered
         * neither connection would form. The rule is in the protocol so that
         * both ends compute the same answer.
         */
        if (shouldOffer(options.myId, peerId)) void offerTo(peerId);
      }
    },

    accept(from, kind, payload) {
      if (closed) return;
      void (async () => {
        if (kind === 'bye') {
          close(from);
          return;
        }

        /*
         * A blob from somebody we have not opened a connection to yet.
         *
         * Ordinary: their roster reached them before ours reached us. Opening
         * on demand means the handshake is not lost to a few milliseconds.
         */
        const peer = peers.get(from) ?? open(from);

        try {
          if (kind === 'offer') {
            /*
             * Handed back exactly as the far browser produced it.
             *
             * Rebuilding a description from its parts invites a field to be
             * dropped in a future browser version, and the server already
             * forwards the blob verbatim — so this end should too.
             */
            await peer.connection.setRemoteDescription(
              JSON.parse(payload) as RTCSessionDescriptionInit,
            );
            peer.remoteSet = true;
            await drain(peer);

            const answer = await peer.connection.createAnswer();
            await peer.connection.setLocalDescription(answer);
            options.signal(from, 'answer', JSON.stringify(answer));
            return;
          }

          if (kind === 'answer') {
            await peer.connection.setRemoteDescription(
              JSON.parse(payload) as RTCSessionDescriptionInit,
            );
            peer.remoteSet = true;
            await drain(peer);
            return;
          }

          if (kind === 'candidate') {
            const candidate = JSON.parse(payload) as RTCIceCandidateInit;
            if (!peer.remoteSet) {
              peer.pending.push(candidate);
              return;
            }
            await peer.connection.addIceCandidate(candidate);
          }
        } catch {
          /*
           * A handshake that cannot be completed is dropped rather than
           * retried, for the same reason a failed connection is: the roster is
           * the trigger, and it has not changed.
           */
          fail('could not connect to somebody at the table');
          close(from);
        }
      })();
    },

    leave() {
      if (closed) return;
      closed = true;

      for (const peerId of [...peers.keys()]) {
        options.signal(peerId, 'bye', '');
        close(peerId);
      }

      /*
       * And the microphone goes back.
       *
       * Leaving audio is not the same as leaving the game, and somebody who
       * leaves the conversation should see the browser's recording indicator go
       * out. Holding the device open because they might come back would be the
       * app keeping a microphone it is not using.
       */
      for (const t of microphone.getTracks()) t.stop();
    },
  };
}
